import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail, fmtINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Empty } from "@/components/UI";
import { toast } from "sonner";

const STAGE_STYLE = {
  yarn:       { color: "bg-amber-100 text-amber-900 border-amber-400", label: "Yarn Purchase" },
  knitting:   { color: "bg-slate-100 text-slate-900 border-slate-400", label: "Knitting" },
  dyeing:     { color: "bg-indigo-100 text-indigo-900 border-indigo-400", label: "Dyeing" },
  bleaching:  { color: "bg-sky-100 text-sky-900 border-sky-400", label: "Bleaching" },
  compacting: { color: "bg-emerald-100 text-emerald-900 border-emerald-400", label: "Compacting" },
  printing:   { color: "bg-fuchsia-100 text-fuchsia-900 border-fuchsia-400", label: "Printing" },
  cutting:    { color: "bg-orange-100 text-orange-900 border-orange-400", label: "Cutting" },
  stitching:  { color: "bg-rose-100 text-rose-900 border-rose-400", label: "Stitching" },
  packing:    { color: "bg-teal-100 text-teal-900 border-teal-400", label: "Packing" },
  dispatch:   { color: "bg-neutral-900 text-white border-black", label: "Dispatch" },
};

function Node({ stageKey, date, title, subtitle, extra, footer, testid }) {
  const s = STAGE_STYLE[stageKey] || { color: "bg-white text-foreground border-border", label: stageKey };
  return (
    <div className="flex gap-3" data-testid={testid}>
      <div className="flex flex-col items-center pt-1">
        <div className={`w-3 h-3 rounded-full border-2 ${s.color.replace(/text-\S+ /, "")} border-current`} />
        <div className="flex-1 w-px bg-border my-2" />
      </div>
      <div className="flex-1 pb-6">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-block label-caps px-2 py-0.5 border ${s.color}`}>{s.label}</span>
          <span className="font-mono text-[11px] text-muted-foreground">{date || "—"}</span>
        </div>
        <div className="font-display font-black text-base mt-1.5">{title}</div>
        {subtitle && <div className="text-xs text-muted-foreground mt-0.5">{subtitle}</div>}
        {extra && <div className="mt-2 text-xs font-mono">{extra}</div>}
        {footer && <div className="mt-2 text-[11px] text-muted-foreground">{footer}</div>}
      </div>
    </div>
  );
}

export default function LotTimelinePage() {
  const { lotNo } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState(lotNo || "");
  const [company, setCompany] = useState({});

  useEffect(() => {
    if (!lotNo) { setLoading(false); return; }
    setLoading(true);
    api.get(`/lots/${encodeURIComponent(lotNo)}/timeline`)
      .then((r) => { setData(r.data); setError(""); })
      .catch((err) => setError(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load lot"))
      .finally(() => setLoading(false));
    setSearch(lotNo);
  }, [lotNo]);

  useEffect(() => {
    api.get("/settings/company").then((r) => setCompany(r.data || {})).catch(() => {});
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    if (search.trim()) nav(`/lots/${encodeURIComponent(search.trim().toUpperCase())}`);
  };

  const printCertificate = () => {
    if (!data) return;
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const totalKg = (data.yarns || []).reduce((s, y) => s + (Number(y.kg) || 0), 0);
    const totalCut = (data.cuttings || []).reduce((s, c) => s + (Number(c.pcs_out) || 0), 0);
    const totalDispatched = (data.dispatches || []).reduce((s, d) => s + (Number(d.total_pcs) || 0), 0);
    const rows = [];
    const push = (stage, date, party, detail, qty) => rows.push({ stage, date, party, detail, qty });
    (data.yarns || []).forEach((y) => push(
      "Yarn Purchase", y.date, y.supplier_name || "—",
      `${esc(y.yarn_type)} · ${y.kg} kg${isAdmin && y.amount ? ` · ${fmtINR(y.amount)}` : ""}`,
      `${y.kg} kg`,
    ));
    (data.dcs || []).forEach((dc) => push(
      `${dc.stage.charAt(0).toUpperCase() + dc.stage.slice(1)}${dc.is_bleaching ? " (Bleach)" : ""} DC`,
      dc.date, dc.party_name || "—",
      `${esc(dc.dc_number)}${dc.is_auto ? " · AUTO" : ""} · sent ${dc.total_sent} kg · recv ${dc.total_received} kg${dc.notes ? ` · ${esc(dc.notes)}` : ""}`,
      `${dc.total_sent} → ${dc.total_received} kg`,
    ));
    (data.cuttings || []).forEach((c) => push(
      "Cutting", c.date, c.worker_name || "Inhouse",
      `${esc(c.product_label)} · fabric ${c.fabric_kg} kg → ${c.pcs_out} pcs (${(c.pcs_out / (c.fabric_kg || 1)).toFixed(2)} pcs/kg)`,
      `${c.pcs_out} pcs`,
    ));
    (data.stitchings || []).forEach((s) => push(
      "Stitching", s.date,
      s.mode === "outsourced" ? (s.party_name || "—") : (s.worker_name || "Inhouse"),
      `${esc(s.product_label)} · ${s.mode || "inhouse"}`,
      `${s.pcs} pcs`,
    ));
    (data.packings || []).forEach((p) => push(
      "Packing", p.date, p.worker_name || "Inhouse",
      `${esc(p.product_label)} · ${p.boxes_used || 0} boxes · L${p.labels_used || 0}/S${p.stickers_used || 0}`,
      `${p.pcs} pcs`,
    ));
    (data.dispatches || []).forEach((d) => push(
      "Dispatch", d.date, d.customer_name || "—",
      `${(d.lines || []).map((l) => `${esc(l.product_label)} · ${l.pcs || 0}pcs`).join(", ")}${d.vehicle_no ? ` · ${esc(d.vehicle_no)}` : ""}${d.eway_bill_no ? ` · e-way ${esc(d.eway_bill_no)}` : ""}`,
      `${d.total_boxes} bx / ${d.total_pcs} pcs`,
    ));
    rows.sort((a, b) => (a.date || "").localeCompare(b.date || ""));

    const rowsHtml = rows.map((r, i) => `
      <tr>
        <td class="c">${i + 1}</td>
        <td class="mono">${esc(r.date || "—")}</td>
        <td class="stage">${esc(r.stage)}</td>
        <td>${esc(r.party)}</td>
        <td class="small">${r.detail}</td>
        <td class="r mono">${esc(r.qty)}</td>
      </tr>
    `).join("");

    const co = company || {};
    const html = `<!doctype html><html><head><meta charset="utf-8"/><title>Traceability Certificate · Lot ${esc(data.lot_no)}</title><style>
      @page { size: A4; margin: 14mm }
      * { box-sizing: border-box }
      body { font: 12px "Helvetica Neue", Arial; color: #111; margin: 0 }
      .head { display: flex; justify-content: space-between; border-bottom: 2px solid #111; padding-bottom: 10px }
      .brand { font-weight: 900; font-size: 22px; letter-spacing: .05em }
      .brand small { display: block; font-size: 10px; color: #555; margin-top: 4px; letter-spacing: .2em }
      .brand .sub { display: block; font-size: 10px; margin-top: 3px }
      .doc { text-align: right }
      .doc .title { font-size: 14px; font-weight: 800; letter-spacing: .2em; text-transform: uppercase }
      .doc .num { font-size: 24px; font-weight: 900; letter-spacing: 0.05em; font-family: "Courier New", monospace }
      .doc .stamp { display: inline-block; margin-top: 8px; padding: 4px 10px; border: 1.5px solid #0369a1; color: #0369a1; font-weight: 800; letter-spacing: .18em; font-size: 10px; text-transform: uppercase }
      .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-top: 16px }
      .summary .box { border: 1px solid #ddd; padding: 10px 12px }
      .summary .lab { font-size: 8.5px; color: #666; text-transform: uppercase; letter-spacing: .2em }
      .summary .val { font-weight: 900; font-size: 22px; font-family: "Courier New", monospace }
      .summary .sub { font-size: 10px; color: #666 }
      table { width: 100%; border-collapse: collapse; margin-top: 16px }
      th { text-align: left; text-transform: uppercase; letter-spacing: .15em; font-size: 9px; padding: 8px 6px; border-bottom: 2px solid #111 }
      td { padding: 8px 6px; border-bottom: 1px dashed #ccc; vertical-align: top }
      .r { text-align: right; font-variant-numeric: tabular-nums }
      .c { text-align: center; width: 28px; color: #999; font-size: 9px }
      .mono { font-family: "Courier New", monospace }
      .stage { font-weight: 700; font-size: 10px; text-transform: uppercase; letter-spacing: .1em; white-space: nowrap }
      .small { font-size: 11px; color: #333 }
      .sign { margin-top: 40px; display: grid; grid-template-columns: 1fr 1fr; gap: 30px }
      .sign .box { border-top: 1px solid #111; padding-top: 6px; font-size: 10px; color: #555; text-transform: uppercase; letter-spacing: .2em }
      .foot { margin-top: 24px; font-size: 9px; color: #888; text-align: center; letter-spacing: .2em; text-transform: uppercase }
      .trace-note { margin-top: 10px; font-size: 9.5px; color: #666; font-style: italic }
    </style></head><body>
      <div class="head">
        <div class="brand">${esc(co.company_name || "SLKC")}
          <small>${esc(co.tagline || "Selvalakshmi Knits & Co.")}</small>
          ${co.address ? `<span class="sub">${esc(co.address)}</span>` : ""}
          ${co.gst_no ? `<span class="sub"><b>GSTIN:</b> ${esc(co.gst_no)}</span>` : ""}
        </div>
        <div class="doc">
          <div class="title">Traceability Certificate</div>
          <div class="num">${esc(data.lot_no)}</div>
          <div class="stamp">Lot Journey · Audit Ready</div>
        </div>
      </div>
      <div class="summary">
        <div class="box"><div class="lab">Total Kg Purchased</div><div class="val">${totalKg}</div><div class="sub">${(data.yarns || []).length} yarn record${(data.yarns || []).length === 1 ? "" : "s"}</div></div>
        <div class="box"><div class="lab">Delivery Challans</div><div class="val">${(data.dcs || []).length}</div><div class="sub">across knit/dye/compact</div></div>
        <div class="box"><div class="lab">Total Pcs Cut</div><div class="val">${totalCut}</div><div class="sub">${(data.cuttings || []).length} cutting job${(data.cuttings || []).length === 1 ? "" : "s"}</div></div>
        <div class="box"><div class="lab">Total Pcs Dispatched</div><div class="val">${totalDispatched}</div><div class="sub">${(data.dispatches || []).length} dispatch${(data.dispatches || []).length === 1 ? "" : "es"}</div></div>
      </div>
      <table>
        <thead><tr><th>#</th><th>Date</th><th>Stage</th><th>Party</th><th>Details</th><th class="r">Qty</th></tr></thead>
        <tbody>${rowsHtml || `<tr><td colspan="6" style="text-align:center;padding:30px;color:#999">No records found for this lot.</td></tr>`}</tbody>
      </table>
      ${data.downstream_traced_via_product ? `<div class="trace-note">Stitching / Packing / Dispatch entries below the Cutting stage are traced via product SKU (not strict lot binding).</div>` : ""}
      <div class="sign">
        <div class="box">Quality / Production Manager</div>
        <div class="box">Authorized Signatory</div>
      </div>
      <div class="foot">Generated on ${new Date().toLocaleString()} · SLKC Manufacturing OS</div>
      <script>window.onload = () => { window.print() }</script>
    </body></html>`;
    const w = window.open("", "_blank", "width=920,height=1080");
    if (!w) return toast.error("Pop-up blocked. Allow pop-ups to print.");
    w.document.write(html);
    w.document.close();
  };

  return (
    <div>
      <PageHeader
        label="Traceability"
        title={lotNo ? `Lot ${lotNo}` : "Lot Timeline"}
        subtitle="Full journey · yarn → knit → dye/bleach → compact → cut → stitch → pack → dispatch"
        right={
          <div className="flex items-center gap-2">
            {data && (
              <button
                onClick={printCertificate}
                data-testid="lot-print-certificate"
                className="h-9 px-4 font-mono text-[11px] tracking-[0.15em] uppercase font-bold bg-foreground text-background hover:-translate-y-[1px] transition-transform"
              >
                ↓ Print Certificate
              </button>
            )}
            <form onSubmit={doSearch} className="flex gap-2">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value.toUpperCase())}
                placeholder="LOT-30S-A12"
                className="h-9 px-3 bg-white border border-border font-mono text-xs w-56"
                data-testid="lot-search-input"
              />
            <button type="submit" data-testid="lot-search-submit" className="h-9 px-4 font-mono text-[11px] tracking-[0.15em] uppercase font-bold bg-foreground text-background">Trace</button>
          </form>
          </div>
        }
      />
      <div className="p-4 md:p-8">
        {loading && <Card title="Loading…"><div className="p-4 text-sm text-muted-foreground">Loading lot journey…</div></Card>}
        {!loading && !lotNo && (
          <Card title="Enter a Lot #">
            <div className="p-4 text-sm text-muted-foreground">
              Paste a lot number above (e.g. <span className="font-mono">LOT-30S-ABCDEF</span>) to trace it end-to-end.
              You can also click any lot tag on the Yarn, Cutting, or Delivery Challan pages.
            </div>
          </Card>
        )}
        {error && <Card title="Not found"><div className="p-4 text-destructive text-sm">{error}</div></Card>}
        {!loading && data && (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <Card title="Traceability Timeline" className="xl:col-span-2">
              <div className="p-3">
                {data.yarns.length === 0 && data.dcs.length === 0 && data.cuttings.length === 0 && (
                  <Empty text="No records found for this lot" />
                )}
                {/* Yarn */}
                {data.yarns.map((y) => (
                  <Node
                    key={y.id}
                    stageKey="yarn"
                    date={y.date}
                    testid={`lot-node-yarn-${y.id}`}
                    title={`${y.yarn_type} · ${y.kg} kg`}
                    subtitle={`Supplier: ${y.supplier_name || "—"}`}
                    extra={<span>
                      Sent to knitting: <b>{y.kg_sent_to_knitting || 0} kg</b> · Stock: <b>{Math.max(0, (y.kg || 0) - (y.kg_sent_to_knitting || 0))} kg</b>
                      {isAdmin && <> · Cost: <b>{fmtINR(y.amount)}</b></>}
                    </span>}
                    footer={y.knitting_dc_number ? <>Auto Knitting DC: <span className="font-mono">{y.knitting_dc_number}</span></> : null}
                  />
                ))}
                {/* DCs */}
                {data.dcs.map((dc) => (
                  <Node
                    key={dc.id}
                    stageKey={dc.is_bleaching ? "bleaching" : dc.stage}
                    date={dc.date}
                    testid={`lot-node-dc-${dc.id}`}
                    title={<><span className="font-mono">{dc.dc_number}</span>{dc.is_auto && <span className="ml-2 text-[9px] font-bold text-accent tracking-widest">AUTO</span>}</>}
                    subtitle={`${dc.party_name || "—"} · ${dc.lines.length} line${dc.lines.length === 1 ? "" : "s"}`}
                    extra={<span>Sent <b>{dc.total_sent}</b> kg · Received <b>{dc.total_received}</b> kg</span>}
                    footer={dc.notes || null}
                  />
                ))}
                {/* Cutting */}
                {data.cuttings.map((c) => (
                  <Node
                    key={c.id}
                    stageKey="cutting"
                    date={c.date}
                    testid={`lot-node-cutting-${c.id}`}
                    title={`${c.product_label} · ${c.pcs_out} pcs`}
                    subtitle={`Cutter: ${c.worker_name || "—"} · Fabric ${c.fabric_kg} kg (${(c.pcs_out / (c.fabric_kg || 1)).toFixed(2)} pcs/kg)`}
                  />
                ))}
                {data.downstream_traced_via_product && (data.stitchings.length + data.packings.length + data.dispatches.length > 0) && (
                  <div className="border-l-2 border-dashed border-border pl-4 py-2 my-2 text-[11px] text-muted-foreground font-mono">
                    Downstream steps below are traced via the products cut from this lot (not strict lot binding).
                  </div>
                )}
                {/* Stitching (via product) */}
                {data.stitchings.map((s) => (
                  <Node
                    key={s.id}
                    stageKey="stitching"
                    date={s.date}
                    testid={`lot-node-stitch-${s.id}`}
                    title={`${s.product_label} · ${s.pcs} pcs`}
                    subtitle={`${s.mode === "outsourced" ? `Outsourced · ${s.party_name || "—"}` : `Inhouse · ${s.worker_name || "—"}`}`}
                  />
                ))}
                {/* Packing (via product) */}
                {data.packings.map((p) => (
                  <Node
                    key={p.id}
                    stageKey="packing"
                    date={p.date}
                    testid={`lot-node-pack-${p.id}`}
                    title={`${p.product_label} · ${p.pcs} pcs`}
                    subtitle={`${p.boxes_used || 0} boxes · Labels ${p.labels_used || 0} · Stickers ${p.stickers_used || 0}`}
                  />
                ))}
                {/* Dispatch (via product) */}
                {data.dispatches.map((d) => (
                  <Node
                    key={d.id}
                    stageKey="dispatch"
                    date={d.date}
                    testid={`lot-node-dispatch-${d.id}`}
                    title={<>{d.customer_name || "—"} · <b>{d.total_boxes}</b> boxes ({d.total_pcs} pcs)</>}
                    subtitle={d.lines.map((l) => `${l.product_label} · ${l.pcs || 0}pcs`).join("  ·  ")}
                    extra={(d.transporter_name || d.vehicle_no || d.eway_bill_no) ? (
                      <span>{d.transporter_name ? `${d.transporter_name}` : ""}{d.vehicle_no ? ` · ${d.vehicle_no}` : ""}{d.eway_bill_no ? ` · e-way ${d.eway_bill_no}` : ""}</span>
                    ) : null}
                  />
                ))}
                {/* Trailing spacer to hide the last connector line */}
                <div className="pl-6 -mt-6 h-4 bg-background"></div>
              </div>
            </Card>

            <Card title="Summary">
              <div className="p-4 space-y-3">
                <SummaryRow label="Lot #" value={<span className="font-mono">{data.lot_no}</span>} />
                <SummaryRow label="Yarn Records" value={data.yarns.length} />
                <SummaryRow label="Delivery Challans" value={data.dcs.length} />
                <SummaryRow label="Cutting Jobs" value={data.cuttings.length} />
                <SummaryRow label="Stitching Jobs (via product)" value={data.stitchings.length} />
                <SummaryRow label="Packing Entries (via product)" value={data.packings.length} />
                <SummaryRow label="Dispatches (via product)" value={data.dispatches.length} />
                <div className="border-t border-border pt-3">
                  <div className="label-caps mb-2">Total Kg Purchased</div>
                  <div className="font-display font-black text-2xl tabular">
                    {(data.yarns || []).reduce((s, y) => s + (Number(y.kg) || 0), 0)}
                  </div>
                </div>
                <div>
                  <div className="label-caps mb-2">Total Pcs Cut</div>
                  <div className="font-display font-black text-2xl tabular">
                    {(data.cuttings || []).reduce((s, c) => s + (Number(c.pcs_out) || 0), 0)}
                  </div>
                </div>
                <div>
                  <div className="label-caps mb-2">Total Pcs Dispatched (via product)</div>
                  <div className="font-display font-black text-2xl tabular">
                    {(data.dispatches || []).reduce((s, d) => s + (Number(d.total_pcs) || 0), 0)}
                  </div>
                </div>
                <div className="border-t border-border pt-3">
                  <Link to="/yarn" className="label-caps hover:underline">← Back to Yarn</Link>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="font-mono font-bold">{value}</span>
    </div>
  );
}
